/**
 * Runtime configuration.
 *
 * CLAUDE.md Part I §4 — environment identity is required config with no default.
 * The console refuses to start without it. We never infer the environment from
 * the hostname, the branch or a build flag: a console that thinks it is staging
 * while pointed at production is the worst bug this repo can ship.
 */

export const ENVIRONMENTS = ['staging', 'production'] as const
export type Environment = (typeof ENVIRONMENTS)[number]

/** Mirrors the backend's WALLET_CELL_ROLE. A standby cell serves reads only. */
export const CELL_ROLES = ['primary', 'standby'] as const
export type CellRole = (typeof CELL_ROLES)[number]

export interface ConsoleConfig {
  readonly environment: Environment
  readonly apiBaseUrl: string
  readonly cellId: string
  readonly cellRole: CellRole
}

/**
 * There is deliberately no identity-provider URL here.
 *
 * There used to be. The console built its sign-in link from it, which sent
 * operators to Ory Network's own hostname — so the session cookie landed on
 * `…oryapis.com` and was never sent to our API. Sign-in appeared to work and
 * every subsequent request was unauthenticated.
 *
 * The backend proxies that surface under `/auth` on its own origin, so the link
 * is derived from `apiBaseUrl` and the prefix is a constant on both sides (see
 * `AUTH_PREFIX` in `lib/auth/session`). Configuration removed rather than
 * corrected: a console that can be pointed at an arbitrary sign-in host is one
 * misconfiguration away from sending an operator's password to a stranger.
 */
export class ConfigError extends Error {
  constructor(missing: readonly string[]) {
    super(
      `Operator console cannot start: invalid or missing runtime configuration ` +
        `(${missing.join(', ')}). These are required and have no defaults — see CLAUDE.md Part I §4.`,
    )
    this.name = 'ConfigError'
  }
}

/** True while the placeholder is still in place, i.e. the entrypoint did not substitute it. */
function unsubstituted(value: unknown): boolean {
  return typeof value === 'string' && value.startsWith('__CONSOLE_') && value.endsWith('__')
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key]
  if (typeof value !== 'string') return null
  if (unsubstituted(value)) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Validates the injected configuration. Pure: takes its input explicitly so it
 * is table-testable, per CLAUDE.md Part III.
 *
 * @throws ConfigError listing every problem at once — an operator fixing a
 * deployment should not have to discover the mistakes one restart at a time.
 */
export function parseConfig(source: unknown): ConsoleConfig {
  const missing: string[] = []

  if (typeof source !== 'object' || source === null) {
    throw new ConfigError(['environment', 'apiBaseUrl', 'cellId', 'cellRole'])
  }
  const raw = source as Record<string, unknown>

  const environment = readString(raw, 'environment')
  if (environment === null || !(ENVIRONMENTS as readonly string[]).includes(environment)) {
    missing.push(`environment (one of ${ENVIRONMENTS.join(' | ')})`)
  }

  const apiBaseUrl = readString(raw, 'apiBaseUrl')
  if (apiBaseUrl === null) {
    missing.push('apiBaseUrl')
  } else {
    try {
      const url = new URL(apiBaseUrl)
      if (url.protocol !== 'https:' && url.hostname !== 'localhost') {
        missing.push('apiBaseUrl (must be https outside localhost)')
      }
    } catch {
      missing.push('apiBaseUrl (must be an absolute URL)')
    }
  }

  const cellId = readString(raw, 'cellId')
  if (cellId === null) missing.push('cellId')

  const cellRole = readString(raw, 'cellRole')
  if (cellRole === null || !(CELL_ROLES as readonly string[]).includes(cellRole)) {
    missing.push(`cellRole (one of ${CELL_ROLES.join(' | ')})`)
  }

  if (missing.length > 0) throw new ConfigError(missing)

  return {
    environment: environment as Environment,
    // Normalised without a trailing slash so path joining stays predictable.
    apiBaseUrl: apiBaseUrl!.replace(/\/+$/, ''),
    cellId: cellId!,
    cellRole: cellRole as CellRole,
  }
}

declare global {
  interface Window {
    __CONSOLE_CONFIG__?: unknown
  }
}

export function loadConfig(): ConsoleConfig {
  return parseConfig(window.__CONSOLE_CONFIG__)
}
